import React, { forwardRef, type ReactNode } from 'react';

import { TouchScrollable } from 'react-scrolllock';

import { cssMap } from '@atlaskit/css';
import { useModal } from '@atlaskit/modal-dialog';
import { Stack } from '@atlaskit/primitives/compiled';

const styles = cssMap({
	container: {
		overflowY: 'auto',
		flex: '1 1 auto',
	},
});

type ModalBodyProps = {
	/**
	 * Children of modal dialog header.
	 */
	children?: ReactNode;

	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
};

export const ModalBody = (props: ModalBodyProps): React.JSX.Element => {
	const { children, testId: userDefinedTestId } = props;
	const { testId: modalTestId } = useModal();

	const testId = userDefinedTestId || (modalTestId && `${modalTestId}--body`);

	return (
		<TouchScrollable>
			<ScrollableBody testId={testId}>{children}</ScrollableBody>
		</TouchScrollable>
	);
};

const ScrollableBody: React.ForwardRefExoticComponent<ModalBodyProps> = forwardRef<
	HTMLElement,
	ModalBodyProps
>(({ testId, children }, ref) => {
	return (
		<Stack space="space.300" xcss={styles.container} testId={testId} ref={ref}>
			{children}
		</Stack>
	);
});
